import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sharesApi } from '@/lib/api';
import type { ShareLink } from '@/types/database';

export const shareKeys = {
  all: ['shares'] as const,
  lists: () => [...shareKeys.all, 'list'] as const,
  list: () => [...shareKeys.lists()] as const,
  details: () => [...shareKeys.all, 'detail'] as const,
  detail: (id: string) => [...shareKeys.details(), id] as const,
  byResource: (type: 'collection' | 'bookmark', resourceId: string) =>
    [...shareKeys.all, 'resource', type, resourceId] as const,
};

// Get all shares for current user
export function useShares() {
  return useQuery({
    queryKey: shareKeys.list(),
    queryFn: () => sharesApi.getAll(),
  });
}

// Get single share by ID
export function useShare(id: string) {
  return useQuery({
    queryKey: shareKeys.detail(id),
    queryFn: () => sharesApi.getById(id),
    enabled: !!id,
  });
}

// Check if a resource is shared
export function useShareByResource(
  resourceType: 'collection' | 'bookmark',
  resourceId: string
) {
  return useQuery({
    queryKey: shareKeys.byResource(resourceType, resourceId),
    queryFn: () => sharesApi.getByResource(resourceType, resourceId),
    enabled: !!resourceId,
  });
}

// Create a new share
export function useCreateShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      resource_type: 'collection' | 'bookmark';
      resource_id: string;
      slug?: string;
      allow_copy?: boolean;
      password?: string;
    }) => sharesApi.create(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
      // Also invalidate the specific resource check
      queryClient.invalidateQueries({
        queryKey: shareKeys.byResource(data.resource_type, data.resource_id),
      });
    },
  });
}

// Update share settings
export function useUpdateShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<{
        slug: string | null;
        is_active: boolean;
        allow_copy: boolean;
        password: string | null;
      }>;
    }) => sharesApi.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
      queryClient.invalidateQueries({ queryKey: shareKeys.detail(data.id) });
    },
  });
}

// Delete a share
export function useDeleteShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sharesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
    },
  });
}

// Helper to generate share URL
export function generateShareUrl(share: ShareLink): string {
  const base = `${window.location.origin}/s/${share.id}`;
  return share.slug ? `${base}/${share.slug}` : base;
}
